import { useEffect, useRef, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import { MAPLIBRE_OSM_STYLE } from "@/lib/map-style";

export type PlaceLocationPin = {
  id: string;
  label?: string | null;
  address?: string | null;
  city_slug?: string | null;
  lat: number;
  lng: number;
};

type MLMap = import("maplibre-gl").Map;
type MLMarker = import("maplibre-gl").Marker;

/**
 * Multi-pin map for a single place's physical locations.
 * All pins are shown at once; the selected pin is highlighted and clicking
 * any pin calls onSelect so the parent can update the details panel without
 * navigating away.
 */
export default function PlaceLocationsMap({
  locations,
  height = 320,
  selectedId,
  onSelect,
}: {
  locations: PlaceLocationPin[];
  height?: number;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const markersRef = useRef<Map<string, { marker: MLMarker; el: HTMLDivElement }>>(new Map());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const [ready, setReady] = useState(false);

  // (Re)build map + markers whenever the set of locations changes.
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    let cancelled = false;
    void import("maplibre-gl").then((mod) => {
      if (cancelled || !ref.current || mapRef.current) return;
      const first = locations[0];
      const map = new mod.Map({
        container: ref.current,
        style: MAPLIBRE_OSM_STYLE,
        center: [first?.lng ?? 24.94, first?.lat ?? 60.17],
        zoom: 11,
        attributionControl: false,
        interactive: true,
      });
      map.addControl(new mod.NavigationControl({ showCompass: false, showZoom: true }), "top-left");
      map.on("load", () => {
        markersRef.current.clear();
        for (const loc of locations) {
          const el = document.createElement("div");
          el.setAttribute("data-loc-id", loc.id);
          el.style.cssText = baseMarkerCss;
          el.textContent = "📍";
          el.addEventListener("click", (e) => {
            e.stopPropagation();
            onSelectRef.current?.(loc.id);
          });
          const marker = new mod.Marker({ element: el }).setLngLat([loc.lng, loc.lat]).addTo(map);
          markersRef.current.set(loc.id, { marker, el });
        }
        if (locations.length > 1) {
          const bounds = new mod.LngLatBounds();
          for (const l of locations) bounds.extend([l.lng, l.lat]);
          map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 0 });
        }
        setReady(true);
      });
      mapRef.current = map;
    });
    return () => {
      cancelled = true;
      markersRef.current.clear();
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations.map((l) => `${l.id}:${l.lat},${l.lng}`).join("|")]);

  // Highlight the selected marker and pan to it (without collapsing others).
  useEffect(() => {
    if (!ready) return;
    for (const [id, { el }] of markersRef.current.entries()) {
      const active = id === selectedId;
      el.style.cssText = active ? activeMarkerCss : baseMarkerCss;
      el.style.zIndex = active ? "3" : "1";
    }
    if (selectedId) {
      const entry = markersRef.current.get(selectedId);
      const map = mapRef.current;
      if (entry && map) {
        const { lng, lat } = entry.marker.getLngLat();
        map.easeTo({ center: [lng, lat], duration: 450 });
      }
    }
  }, [selectedId, ready]);

  return (
    <div
      ref={ref}
      style={{ height }}
      className="w-full rounded-2xl overflow-hidden border border-navy/15 shadow-sm bg-white"
      aria-label={`Map of ${locations.length} location${locations.length === 1 ? "" : "s"}`}
    >
      {!ready && <div className="p-4 text-sm text-navy/60">Loading map…</div>}
    </div>
  );
}

const baseMarkerCss =
  "width:28px;height:28px;border-radius:50%;background:#E11D48;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;cursor:pointer;transition:transform .18s ease, box-shadow .18s ease;";

const activeMarkerCss =
  "width:38px;height:38px;border-radius:50%;background:#0F766E;border:3px solid #fff;box-shadow:0 4px 14px rgba(15,118,110,.55);display:flex;align-items:center;justify-content:center;color:#fff;font-size:18px;cursor:pointer;transform:translateY(-2px);transition:transform .18s ease, box-shadow .18s ease;";
